import { AlertTriangle, RotateCw } from 'lucide-react';
import { useChatStore } from '@/stores/chatStore';

interface ErrorMessageProps {
  error: string;
}

function humanize(raw: string): string {
  const lower = raw.toLowerCase();

  if (lower.includes('503') || lower.includes('service unavailable') || lower.includes('high demand') || lower.includes('overloaded')) {
    return "I can't process that right now — the service is busy. Try again in a moment?";
  }
  if (lower.includes('429') || lower.includes('rate limit') || lower.includes('too many requests')) {
    return "I'm sorry — I'm hitting my rate limit right now. Mind waiting a few seconds before you retry?";
  }
  if (lower.includes('db error') || lower.includes('column') || lower.includes('does not exist') || lower.includes('sql') || lower.includes('database')) {
    return "I'm sorry — I ran into a snag pulling up your account context. Please try again in a moment.";
  }
  if (lower.includes('network') || lower.includes('failed to fetch') || lower.includes('econnrefused')) {
    return "I'm sorry — I can't reach my services right now. Could you check your connection and try again?";
  }
  if (lower.includes('timeout') || lower.includes('timed out')) {
    return "I'm sorry — that took longer than I expected. Want me to try again?";
  }
  if (lower.includes('page understanding')) {
    return "I had trouble reading the page just now. Could you tell me which button or field to use?";
  }
  if (lower.includes('browser session became invalid')) {
    return "I'm sorry — I lost track of the browser state mid-action. Want me to try once more?";
  }
  if (lower.includes('browser action') || lower.includes('browser ai call') || lower.includes('browser disconnected') || lower.includes('browser not ready') || lower.includes('browser session not ready')) {
    return "I'm sorry — that browser action didn't go through. Could you try once more, or break the request into smaller steps?";
  }
  if (lower.includes('response was empty')) {
    return "I worked through several steps but didn't put together a clean answer. Try once more with a more specific request?";
  }
  // The backend already crafts some user-facing sentences (e.g. the tool-loop
  // circuit breaker: "I couldn't complete that action — it may not be available
  // yet…"). Pass those through verbatim instead of clobbering the specific,
  // actionable message into the generic fallback below.
  if (lower.includes("couldn't complete") || lower.includes('may not be available') || lower.includes('not available yet')) {
    return raw;
  }
  return "I'm sorry — something went wrong on my end. Want me to try again?";
}

export function ErrorMessage({ error }: ErrorMessageProps) {
  const retryLastMessage = useChatStore((s) => s.retryLastMessage);
  const clearError = useChatStore((s) => s.clearError);
  const lastUserMessage = useChatStore((s) => s.lastUserMessage);

  const message = humanize(error);
  const canRetry = !!lastUserMessage;

  return (
    <div className="flex items-end gap-2 py-1.5">
      <video
        src="/zac-avatar.mp4"
        autoPlay
        loop
        muted
        playsInline
        className="h-7 w-7 shrink-0 rounded-lg object-cover"
        aria-label="ZAC"
      />
      <div className="max-w-[85%] rounded-tl-2xl rounded-tr-2xl rounded-br-2xl rounded-bl-sm border border-red-900/40 bg-red-900/10 px-4 py-3 text-foreground">
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
          <div className="flex-1 min-w-0">
            <p className="text-sm">{message}</p>
            <div className="mt-3 flex items-center gap-2">
              {canRetry && (
                <button
                  type="button"
                  onClick={retryLastMessage}
                  className="flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs text-foreground transition-colors hover:bg-secondary/80"
                >
                  <RotateCw className="h-3 w-3" />
                  Try again
                </button>
              )}
              <button
                type="button"
                onClick={clearError}
                className="rounded-full px-3 py-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
